import React, { useEffect, useState } from 'react';
import CompetitiveSelect from './competitive-select-box';
import { CompetitorInfoResponse, CompetitorOffer, fetchCompetitorInfo } from './service';

interface CompetitorInfoWithId extends CompetitorInfoResponse {
  competitorId: string;
}

const CompetitiveInfo: React.FC = () => {
  const [isCompetitorOpen, setIsCompetitorOpen] = useState<{
    [key: string]: boolean;
  }>({});
  const [competitorInfo, setCompetitorInfo] = useState<CompetitorInfoWithId[] | null>(null);
  const [selectedPlans, setSelectedPlans] = useState<{ [key: string]: CompetitorOffer | null }>({});

  const zipCode = "37660";

  useEffect(() => {
    const fetchCompetitorData = async () => {
      try {
        const response = await fetchCompetitorInfo(zipCode);
        const responseWithId = response.map((item, index) => ({
          ...item,
          competitorId: `${item.competitor}-${index}`
        }));
        setCompetitorInfo(responseWithId);
      } catch (error) {
        console.error('Error fetching competitor info:', error);
      }
    };
    fetchCompetitorData();
  }, [zipCode]);

  useEffect(() => {
    if (competitorInfo && competitorInfo.length > 0) {
      const initialPlans: { [key: string]: CompetitorOffer } = {};
      competitorInfo.forEach(info => {
        initialPlans[info.competitorId] = info.competitor_intelligence[0];
      });
      setSelectedPlans(initialPlans);
    }
  }, [competitorInfo]);

  const toggleAccordion = (competitorId: string) => {
    setIsCompetitorOpen((prev) => ({ ...prev, [competitorId]: !prev[competitorId] }));
  };

  const handlePlanSelect = (competitorId: string, selectedSpeed: string) => {
    const selected = competitorInfo?.find(info =>
      info.competitorId === competitorId
    )?.competitor_intelligence.find(plan => plan.download_speed === selectedSpeed);

    if (selected) {
      setSelectedPlans(prev => ({ ...prev, [competitorId]: selected }));
    }
  };

  return (
    <div className="relative bg-[#7EDEF1] rounded-b-xl min-h-72">
      {competitorInfo?.map(info => (
        <div key={info.competitorId} className="rounded-xl relative bg-[#97EEFF] flex flex-col items-center mb-3">
          <div
            onClick={() => toggleAccordion(info.competitorId)}
            className="flex items-center justify-between p-3 w-full rounded-xl focus:outline-none cursor-pointer"
          >
            <span className="text-sm font-semibold">{info.competitor}</span>
            <div className="w-6 h-6 flex justify-center items-center">
              {isCompetitorOpen[info.competitorId] ? (
                <span className="w-6 h-6 bg-[#7EDEF1] rounded-full flex items-center justify-center cursor-pointer">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
                  </svg>
                </span>
              ) : (
                <span className="w-6 h-6 bg-[#97EEFF] rounded-full flex items-center justify-center cursor-pointer">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                </span>
              )}
            </div>
          </div>
          {isCompetitorOpen[info.competitorId] && (
            <div className="flex-1 w-full px-3 mb-3 relative">
              <CompetitiveSelect
                offers={info.competitor_intelligence}
                defaultSelected={selectedPlans[info.competitorId]?.download_speed || info.competitor_intelligence[0].download_speed}
                onSelect={(selectedSpeed) => handlePlanSelect(info.competitorId, selectedSpeed)}
              />
              {selectedPlans[info.competitorId] && (
                <div className={`items-center text-xs my-3`}>
                  <p className='font-semibold mb-2'>Talking Points:</p>
                  <p dangerouslySetInnerHTML={{ __html: selectedPlans[info.competitorId]?.talking_points?.instruction || '' }} />
                  <div className='my-2 flex flex-col gap-1'>
                    {selectedPlans[info.competitorId]?.talking_points?.dialog_script.map((script, index) => (
                      <p className='text-black text-xs m-0 leading-[18px] font-medium items-stretch bg-[#E6F7FF]/40 rounded-t-3xl rounded-bl-3xl p-3 mb-1' key={index} dangerouslySetInnerHTML={{ __html: script }} />
                    ))}
                  </div>
                  <div className="flex flex-col gap-1">
                    <p className='font-semibold my-2'>Price_Offer:</p>
                    {selectedPlans[info.competitorId]?.speed && <p><span className="font-semibold">Speed :</span> {selectedPlans[info.competitorId]?.speed}</p>}
                    {selectedPlans[info.competitorId]?.offer_price !== undefined && <p><span className="font-semibold">Offer Price :</span> {selectedPlans[info.competitorId]?.offer_price}</p>}
                    {selectedPlans[info.competitorId]?.offer_name && <p><span className="font-semibold">Offer Name :</span> {selectedPlans[info.competitorId]?.offer_name}</p>}
                    {selectedPlans[info.competitorId]?.access_type && <p><span className="font-semibold">Access Type :</span> {selectedPlans[info.competitorId]?.access_type}</p>}
                    {selectedPlans[info.competitorId]?.notes && <p><span className="font-semibold">Notes: </span> {selectedPlans[info.competitorId]?.notes}</p>}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default CompetitiveInfo;
